import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMG } from "@/data/content";

const lineAnim = (i) => ({
  initial: { y: "112%" },
  animate: { y: "0%" },
  transition: { duration: 1.2, delay: 0.45 + i * 0.2, ease: [0.16, 1, 0.3, 1] },
});

const fadeAnim = (delay) => ({
  initial: { opacity: 0, y: 18 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, delay, ease: [0.16, 1, 0.3, 1] },
});

export default function Hero({ onPlan, onExplore }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);

  return (
    <section id="home" ref={ref} data-testid="hero-section" className="relative h-svh min-h-[620px] overflow-hidden bg-forest">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <motion.img
          src={IMG.hero}
          alt="Sunset over a turquoise lagoon with overwater villas in the Maldives"
          className="h-full w-full object-cover"
          initial={{ scale: 1.12 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1] }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-forest/70 via-forest/40 to-forest/85" />
        <div className="absolute inset-0 bg-forest/10" />
      </motion.div>

      <svg
        className="pointer-events-none absolute bottom-[16%] left-0 h-40 w-full opacity-40"
        viewBox="0 0 1440 160"
        fill="none"
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <motion.path
          d="M-20 120 C 360 20, 780 200, 1460 60"
          stroke="#C5A059"
          strokeWidth="1"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2.6, delay: 1.4, ease: "easeInOut" }}
        />
      </svg>

      <motion.div
        style={{ opacity: contentOpacity, y: contentY }}
        className="relative z-10 mx-auto flex h-full max-w-5xl flex-col items-center justify-center px-6 text-center"
      >
        <motion.p
          {...fadeAnim(0.3)}
          className="mb-7 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-[10px] md:text-xs uppercase tracking-widest2 font-medium text-gold"
        >
          <span className="hidden sm:inline h-px w-10 bg-gold/70" aria-hidden="true" />
          <span>Mirevaa Journeys · Curated Journeys. Personally Designed.</span>
          <span className="hidden sm:inline h-px w-10 bg-gold/70" aria-hidden="true" />
        </motion.p>

        <h1 className="font-display font-light text-ivory [text-shadow:0_2px_28px_rgba(10,37,32,0.55)]">
          <span className="block overflow-hidden pb-[0.22em] -mb-[0.12em]">
            <motion.span {...lineAnim(0)} className="block text-5xl leading-[1.08] sm:text-7xl md:text-8xl">
              Travel the world,
            </motion.span>
          </span>
          <span className="block overflow-hidden pb-[0.22em] -mb-[0.06em]">
            <motion.span {...lineAnim(1)} className="block text-5xl leading-[1.08] italic text-gold sm:text-7xl md:text-8xl">
              your way.
            </motion.span>
          </span>
        </h1>

        <motion.p {...fadeAnim(1.05)} className="mt-7 max-w-xl text-sm leading-relaxed text-ivory/85 md:text-lg">
          Thoughtfully crafted international journeys, personalised around your dreams, interests and way of travelling.
        </motion.p>

        <motion.div {...fadeAnim(1.3)} className="mt-11 flex flex-col items-center gap-4 sm:flex-row">
          <button
            data-testid="hero-cta-plan"
            onClick={onPlan}
            className="bg-gold px-11 py-4 text-[11px] uppercase tracking-widest2 font-medium text-forest transition-colors duration-500 hover:bg-ivory"
          >
            Plan Your Journey
          </button>
          <button
            data-testid="hero-cta-explore"
            onClick={onExplore}
            className="border border-ivory/40 px-11 py-4 text-[11px] uppercase tracking-widest2 font-medium text-ivory transition-colors duration-500 hover:border-gold hover:text-gold"
          >
            Explore Destinations
          </button>
        </motion.div>
      </motion.div>

      <motion.div
        {...fadeAnim(2)}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
        data-testid="hero-scroll-cue"
      >
        <div className="flex flex-col items-center gap-3">
          <span className="text-[9px] uppercase tracking-widest2 text-ivory/60">Scroll</span>
          <div className="h-12 w-px overflow-hidden bg-ivory/20">
            <motion.div
              className="h-1/2 w-px bg-gold"
              animate={{ y: ["-100%", "200%"] }}
              transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
}
